import type { RDFQuad } from '@/types'

// Global hylar declarations
declare global {
  interface Window {
    hylarCore: {
      incremental: (facts: any[], additions: any[], deletions: any[], insertions: any[], ruleset: any) => Promise<{additions: any, deletions: any}>
      quadsToFacts: (quads: any[]) => any[]
      factsToQuads: (facts: any) => {explicit: any[], implicit: any[]}
      owl2rl: any
    }
  }
}

export interface ReasoningResult {
  explicitTriples: string[]
  implicitTriples: string[]
  error?: string
}

export class HylarReasoner {
  /**
   * Perform OWL-RL reasoning on RDF quads and return explicit/implicit triples
   */
  static async performReasoning(quads: RDFQuad[]): Promise<ReasoningResult> {
    try {
      let hylarCore = window.hylarCore
      
      if (!hylarCore) {
        try {
          // Try importing from node_modules if global is not available
          const module = await import('hylar-core')
          hylarCore = module.default || module
        } catch (e) {
          console.warn('Failed to import hylar-core:', e)
        }
      }

      if (!hylarCore) {
        throw new Error('Hylar reasoner not available. Please ensure hylar-client.js is loaded or hylar-core is installed.')
      }

      // Convert quads to facts format for hylar
      const facts = hylarCore.quadsToFacts(quads)
      
      // Perform incremental reasoning with OWL-RL rules
      const { additions } = await hylarCore.incremental(facts, [], [], [], hylarCore.owl2rl)
      
      if (!additions) {
        return {
          explicitTriples: [],
          implicitTriples: [],
          error: 'No reasoning results returned'
        }
      }

      // Convert back to quads
      const processedFacts = hylarCore.factsToQuads(additions)

      // The explicit triples are the input, so list them from the parsed
      // quads rather than Hylar's copy. Hylar keeps literals as strings and,
      // reading them back, does not know RDF 1.2 text direction: "x"@ar--rtl
      // would return with the direction lost and a garbled datatype.
      const explicitTriples = this.quadsToNTriples(quads)
      const implicitTriples = this.quadsToNTriples(processedFacts.implicit || [])

      return {
        explicitTriples,
        implicitTriples
      }
    } catch (error) {
      return {
        explicitTriples: [],
        implicitTriples: [],
        error: `Reasoning error: ${error}`
      }
    }
  }

  /**
   * Convert quads to N-Triples format strings
   */
  private static quadsToNTriples(quads: any[]): string[] {
    const ntriples: string[] = []
    
    for (const quad of quads) {
      try {
        const ntripleString = this.quadToNTriple(quad)
        if (ntripleString) {
          ntriples.push(ntripleString)
        }
      } catch (error) {
        console.warn('Error converting quad to N-Triple:', error, quad)
      }
    }
    
    return ntriples
  }

  /**
   * Convert a single quad to N-Triple format
   */
  private static quadToNTriple(quad: any): string {
    if (!quad || !quad.subject || !quad.predicate || !quad.object) {
      return ''
    }

    const subject = this.termToNTriple(quad.subject)
    const predicate = this.termToNTriple(quad.predicate)
    const object = this.termToNTriple(quad.object)

    return `${subject} ${predicate} ${object} .`
  }

  /**
   * Convert an RDF term to N-Triple format
   */
  private static termToNTriple(term: any): string {
    if (!term) return ''

    switch (term.termType) {
      case 'NamedNode':
        return `<${term.value}>`
      
      case 'BlankNode':
        return `_:${term.value}`
      
      case 'Literal': {
        let literal = `"${this.escapeLiteral(term.value)}"`

        if (term.language) {
          literal += `@${term.language}`
          // RDF 1.2 initial text direction rides on the language tag
          if (term.direction) literal += `--${term.direction}`
        } else if (term.datatype && term.datatype.value !== 'http://www.w3.org/2001/XMLSchema#string') {
          literal += `^^<${term.datatype.value}>`
        }

        return literal
      }

      // RDF 1.2 triple term, in N-Triples 1.2 syntax
      case 'Quad':
        return `<<( ${this.termToNTriple(term.subject)} ${this.termToNTriple(term.predicate)} ${this.termToNTriple(term.object)} )>>`

      default:
        return `<${term.value || term}>`
    }
  }

  /**
   * Escape special characters in literal values
   */
  private static escapeLiteral(value: string): string {
    return value
      .replace(/\\/g, '\\\\')  // Escape backslashes
      .replace(/"/g, '\\"')    // Escape quotes
      .replace(/\n/g, '\\n')   // Escape newlines
      .replace(/\r/g, '\\r')   // Escape carriage returns
      .replace(/\t/g, '\\t')   // Escape tabs
  }

  /**
   * Open a new window to display reasoning results
   */
  static displayReasoningResults(result: ReasoningResult): void {
    const windowFeatures = 'width=1020,height=800,resizable=yes,scrollbars=yes'
    const resultWindow = window.open('', 'reasoning-results', windowFeatures)
    
    if (!resultWindow) {
      alert('Could not open results window. Please allow popups for this site.')
      return
    }

    resultWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>OWL-RL Reasoning Results</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5; 
            }
            h2 { 
              color: #333; 
              border-bottom: 2px solid #007acc; 
              padding-bottom: 5px; 
            }
            textarea { 
              width: 100%; 
              height: 300px; 
              font-family: 'Courier New', monospace; 
              font-size: 12px; 
              border: 1px solid #ccc; 
              border-radius: 4px; 
              padding: 10px; 
              background-color: white;
            }
            .error { 
              color: #d32f2f; 
              background-color: #ffebee; 
              padding: 10px; 
              border-radius: 4px; 
              border: 1px solid #d32f2f; 
            }
            .stats { 
              background-color: #e3f2fd; 
              padding: 10px; 
              border-radius: 4px; 
              border: 1px solid #1976d2; 
              margin-bottom: 20px; 
            }
          </style>
        </head>
        <body>
          <h1>OWL-RL Reasoning Results</h1>

          <div class="error" id="error" hidden><strong>Error:</strong> <span id="error-text"></span></div>

          <div class="stats">
            <strong>Statistics:</strong><br>
            Explicit Triples: <span id="explicit-count"></span><br>
            Implicit (Inferred) Triples: <span id="implicit-count"></span><br>
            Total: <span id="total-count"></span>
          </div>

          <h2>Explicit Triples</h2>
          <textarea readonly id="explicit"></textarea>

          <h2>Implicit (Inferred) Triples</h2>
          <textarea readonly id="implicit"></textarea>

          <br><br>
          <button onclick="window.close()">Close Window</button>
        </body>
      </html>
    `)

    resultWindow.document.close()

    // The shell above is fixed markup. Everything that comes from the data
    // (triples, and error messages that may quote it) is set as text, never
    // written as HTML: a literal containing "</textarea><img onerror=...>"
    // would otherwise run in this window, which shares the app's origin.
    const doc = resultWindow.document
    const setText = (id: string, text: string) => {
      const el = doc.getElementById(id)
      if (el) el.textContent = text
    }
    if (result.error) {
      setText('error-text', result.error)
      doc.getElementById('error')?.removeAttribute('hidden')
    }
    setText('explicit-count', String(result.explicitTriples.length))
    setText('implicit-count', String(result.implicitTriples.length))
    setText('total-count', String(result.explicitTriples.length + result.implicitTriples.length))
    setText('explicit', result.explicitTriples.join('\n'))
    setText('implicit', result.implicitTriples.join('\n'))
  }
}